import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { FadeUp } from "@/components/motion";
import { fmtDate } from "@/pages/Blog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const setMeta = (content) => {
  let m = document.querySelector('meta[name="description"]');
  if (!m) {
    m = document.createElement("meta");
    m.setAttribute("name", "description");
    document.head.appendChild(m);
  }
  m.setAttribute("content", content);
};

const DEFAULT_TITLE = "SanCub — Architectural Tapware & Bathroom Fixtures";
const DEFAULT_DESC = "Tapware, showers, baths, basins and LED mirrors — precision-engineered for the modern Australian home.";

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    setPost(null);
    setMissing(false);
    axios
      .get(`${API}/blog/${slug}`)
      .then(({ data }) => {
        setPost(data);
        document.title = `${data.title} — SanCub`;
        if (data.meta_description) setMeta(data.meta_description);
      })
      .catch(() => setMissing(true));
    window.scrollTo(0, 0);
    return () => {
      document.title = DEFAULT_TITLE;
      setMeta(DEFAULT_DESC);
    };
  }, [slug]);

  if (missing) {
    return (
      <main className="flex min-h-[70vh] items-center justify-center px-6 pt-24" data-testid="blog-not-found">
        <div className="text-center">
          <h1 className="font-display text-4xl font-medium tracking-tight text-ink">Article not found.</h1>
          <p className="mt-3 text-sm text-ink/50">It may have been removed or the link is incorrect.</p>
          <Link
            to="/blog"
            data-testid="blog-404-back"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.02] hover:bg-ink/90"
          >
            <ArrowLeft className="h-4 w-4" /> Back to Journal
          </Link>
        </div>
      </main>
    );
  }

  if (!post) {
    return (
      <main className="mx-auto max-w-3xl px-6 pb-28 pt-32 md:pt-40" data-testid="blog-post-loading">
        <div className="h-4 w-32 animate-pulse rounded bg-ink/5" />
        <div className="mt-6 h-12 w-3/4 animate-pulse rounded bg-ink/5" />
        <div className="mt-10 h-72 animate-pulse rounded-[1.5rem] bg-ink/5" />
      </main>
    );
  }

  return (
    <main className="pb-28 pt-32 md:pt-40" data-testid="blog-post-page">
      <article className="mx-auto max-w-3xl px-6">
        <FadeUp>
          <Link
            to="/blog"
            data-testid="blog-back"
            className="group inline-flex items-center gap-2 text-sm font-medium text-ink/50 transition-colors duration-300 hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Journal
          </Link>
          <p className="mt-10 text-[12px] font-semibold uppercase tracking-[0.2em] text-ink/45">
            {fmtDate(post.published_at)}
          </p>
          <h1 className="mt-4 font-display text-4xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl" data-testid="blog-post-title">
            {post.title}
          </h1>
          {post.tags?.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="rounded-full bg-white px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-ink/50 shadow-[0_4px_16px_rgb(0,0,0,0.04)]">
                  {t}
                </span>
              ))}
            </div>
          )}
        </FadeUp>

        {post.image_url && (
          <FadeUp delay={0.15}>
            <div className="mt-10 overflow-hidden rounded-[1.5rem] bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <img src={post.image_url} alt={post.title} className="w-full object-cover" data-testid="blog-post-cover" />
            </div>
          </FadeUp>
        )}

        <FadeUp delay={0.25}>
          <div
            className="blog-content mt-10"
            data-testid="blog-post-content"
            dangerouslySetInnerHTML={{ __html: post.content_html || "" }}
          />
        </FadeUp>

        <FadeUp delay={0.3}>
          <div className="mt-16 rounded-[2rem] bg-ink p-8 text-center md:p-12" data-testid="blog-cta">
            <h2 className="font-display text-2xl font-medium tracking-tight text-white md:text-3xl">
              See it in person.
            </h2>
            <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-white/60">
              Browse the full SanCub collection or visit our Truganina showroom.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/collections" data-testid="blog-cta-collections" className="rounded-full bg-white px-7 py-3.5 text-[13px] font-semibold text-ink transition duration-300 hover:scale-[1.02]">
                Browse collections
              </Link>
              <Link to="/contact" data-testid="blog-cta-contact" className="rounded-full border border-white/20 px-7 py-3.5 text-[13px] font-semibold text-white transition duration-300 hover:bg-white/10">
                Enquire
              </Link>
            </div>
          </div>
        </FadeUp>
      </article>
    </main>
  );
};

export default BlogPost;
